import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CheckCircle, PlayArrow } from '@mui/icons-material';
import { IngestionBrandInfo, IngestionJobType, TriggerIngestionPayload } from '../../services/integrationApi';
import { parseProductUrls, splitUrlDraft, validateProductUrl } from './BrandTriggerDialog';
import { ChipInput, NumberField } from '../ui';
import { tokens } from '../../theme/tokens';
import { ForceStartConfirm } from './ForceStartConfirm';

export type GlobalJobMode =
  | 'full_scrape'
  | 'incremental_scrape'
  | 'rescrape_missing'
  | 'price_refresh'
  | 'stock_refresh'
  | 'market_pricing_refresh';

interface GlobalTriggerDialogProps {
  open: boolean;
  onClose: () => void;
  onTrigger: (brandId: string, payload: TriggerIngestionPayload) => void;
  onTriggerPriceRefresh: (brandId?: string, region?: 'US' | 'CA') => void;
  onTriggerMarketPricing: (brandId?: string) => void;
  onTriggerStockRefresh: (brandId?: string, region?: 'US' | 'CA') => void;
  isPending: boolean;
  cloudRunAvailable?: boolean;
  triggerResult?: { executionMethod?: string; runCommand?: string } | null;
  errorMessage?: string | null;
  errorJobId?: string | null;
  brands: Array<
    Pick<IngestionBrandInfo, 'id' | 'name' | 'categories' | 'scraperStatus' | 'cloudRunEnabled'> &
      Partial<Pick<IngestionBrandInfo, 'isRunning' | 'stats' | 'runningSince'>>
  >;
  initialBrandId?: string;
  hasRunningJob?: boolean;
  runningBrandName?: string;
}

export const GlobalTriggerDialog: React.FC<GlobalTriggerDialogProps> = ({
  open,
  onClose,
  onTrigger,
  onTriggerPriceRefresh,
  onTriggerMarketPricing,
  onTriggerStockRefresh,
  isPending,
  cloudRunAvailable,
  triggerResult,
  errorMessage,
  errorJobId,
  brands,
  initialBrandId,
  hasRunningJob,
  runningBrandName,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const activeBrands = brands.filter((b) => b.scraperStatus === 'active' || b.scraperStatus === 'ready');
  // With nothing preselected, open on the brand that is running: it is the one Force start applies to.
  const runningActiveBrand = activeBrands.find((b) => b.isRunning || (b.stats?.running || 0) > 0);
  const defaultBrand = initialBrandId && activeBrands.some((b) => b.id === initialBrandId)
    ? initialBrandId
    : runningActiveBrand?.id || activeBrands[0]?.id || '';
  const [selectedBrand, setSelectedBrand] = useState<string>(defaultBrand);

  useEffect(() => {
    if (open && defaultBrand) {
      setSelectedBrand(defaultBrand);
    }
  }, [open, defaultBrand]);
  const [jobMode, setJobMode] = useState<GlobalJobMode>('full_scrape');
  const [limit, setLimit] = useState<string>('');
  const [category, setCategory] = useState<string>('all');
  const [hostImages, setHostImages] = useState(true);
  const [region, setRegion] = useState<'US' | 'CA'>('US');
  const [priceRefreshAll, setPriceRefreshAll] = useState(false);
  const [urlsText, setUrlsText] = useState('');
  const parsedUrls = useMemo(() => parseProductUrls(urlsText), [urlsText]);
  const hasTargetUrls = parsedUrls.length > 0;

  useEffect(() => {
    if (!open) setUrlsText('');
  }, [open]);

  const selectedBrandInfo = brands.find((b) => b.id === selectedBrand);
  const isBulkJobMode =
    jobMode === 'price_refresh' || jobMode === 'stock_refresh' || jobMode === 'market_pricing_refresh';

  const [forceConfirmOpen, setForceConfirmOpen] = useState(false);
  useEffect(() => {
    if (!open) setForceConfirmOpen(false);
  }, [open]);

  const buildPayload = (force: boolean): TriggerIngestionPayload => ({
    type: hasTargetUrls ? 'full_scrape' : (jobMode as IngestionJobType),
    limit: hasTargetUrls ? undefined : limit ? parseInt(limit, 10) : undefined,
    category: hasTargetUrls || category === 'all' ? undefined : category,
    hostImages,
    region,
    ...(hasTargetUrls ? { urls: parsedUrls } : {}),
    ...(force
      ? { force: true, ...(selectedBrandInfo?.runningSince ? { expectedRunningSince: selectedBrandInfo.runningSince } : {}) }
      : {}),
  });

  // Force start closes the SELECTED brand's own running record, so it is offered only when that
  // brand reads as running (another brand's run is not its blocker) and only for a single-brand
  // scrape: the bulk refresh modes skip a brand that is running.
  const selectedBrandRunning = Boolean(selectedBrandInfo?.isRunning || (selectedBrandInfo?.stats?.running || 0) > 0);
  const canForceStart = selectedBrandRunning && !isBulkJobMode && Boolean(selectedBrand) && !triggerResult;

  const handleSubmit = () => {
    if (hasRunningJob) return;
    const targetBrand = priceRefreshAll ? undefined : selectedBrand;
    if (jobMode === 'price_refresh') {
      onTriggerPriceRefresh(targetBrand, region);
    } else if (jobMode === 'market_pricing_refresh') {
      onTriggerMarketPricing(targetBrand);
    } else if (jobMode === 'stock_refresh') {
      onTriggerStockRefresh(targetBrand, region);
    } else {
      onTrigger(selectedBrand, buildPayload(false));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>Trigger Product Ingestion</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {hasRunningJob && (
          <Alert severity="info">
            An ingestion job is running{runningBrandName ? ` for ${runningBrandName}` : ''}. New runs are
            temporarily disabled. If that run looks stuck, select {runningBrandName || 'the running brand'} and use Force start.
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error">
            {errorMessage}
            {errorJobId && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Running Job ID: <code>{errorJobId}</code>
              </Typography>
            )}
          </Alert>
        )}
        {triggerResult ? (
          <Box>
            {triggerResult.executionMethod === 'cloud_run' || triggerResult.executionMethod === 'immediate' ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Job started{triggerResult.executionMethod === 'cloud_run' ? ' on Cloud Run' : ''}! It will run automatically.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Job created. Run the following command locally to start scraping:
                <Box
                  component="code"
                  sx={{
                    display: 'block',
                    mt: 1,
                    p: 1,
                    bgcolor: 'grey.900',
                    color: tokens.signal.positive.solid,
                    borderRadius: 1,
                    fontSize: '0.85em',
                    fontFamily: tokens.font.mono,
                  }}
                >
                  {triggerResult.runCommand}
                </Box>
              </Alert>
            )}
          </Box>
        ) : (
          <>
            <FormControl fullWidth>
              <InputLabel>Brand</InputLabel>
              <Select
                value={selectedBrand}
                label="Brand"
                onChange={(e) => {
                  setSelectedBrand(e.target.value);
                  setCategory('all');
                }}
              >
                {activeBrands.map((brand) => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                    {brand.scraperStatus === 'ready' && (
                      <Chip size="small" label="Beta" sx={{ ml: 1 }} color="warning" />
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Region</InputLabel>
              <Select
                value={region}
                label="Region"
                onChange={(e) => setRegion(e.target.value as 'US' | 'CA')}
              >
                <MenuItem value="US">US (USD)</MenuItem>
                <MenuItem value="CA">Canada (CAD)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Job Type</InputLabel>
              <Select
                value={jobMode}
                label="Job Type"
                onChange={(e) => setJobMode(e.target.value as GlobalJobMode)}
              >
                <MenuItem value="full_scrape">Full Scrape</MenuItem>
                <MenuItem value="incremental_scrape">Incremental (New Products Only)</MenuItem>
                <MenuItem value="rescrape_missing">Re-scrape Missing Images</MenuItem>
                <MenuItem value="price_refresh">Price Refresh Only</MenuItem>
                <MenuItem value="stock_refresh">Stock & Price Re-check</MenuItem>
                <MenuItem value="market_pricing_refresh">Market Pricing Refresh</MenuItem>
              </Select>
            </FormControl>

            {isBulkJobMode && (
              <FormControlLabel
                control={
                  <Switch checked={priceRefreshAll} onChange={(e) => setPriceRefreshAll(e.target.checked)} />
                }
                label="Refresh all brands at once"
              />
            )}

            {jobMode === 'price_refresh' && (
              <Alert severity="info">
                Price refresh compares current prices against existing staged and canonical products. Only
                pricing fields are updated — no new products are created.
              </Alert>
            )}

            {jobMode === 'stock_refresh' && (
              <Alert severity="info">
                On-demand re-check of manufacturer stock and pricing on existing products. The weekly
                scheduled stock pass is retired — daily price refresh already collects stock.
              </Alert>
            )}

            {jobMode === 'market_pricing_refresh' && (
              <Alert severity="info">
                Refreshes dealer pricing (B&amp;H, Adorama, etc.) and market MSRP using AI-powered search. Only
                updates products with stale (&gt;7 day old) market pricing data.
              </Alert>
            )}

            {!isBulkJobMode && (
              <>
                <FormControl fullWidth disabled={hasTargetUrls}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={category}
                    label="Category"
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {selectedBrandInfo?.categories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <NumberField
                  label="Limit (optional)"
                  integer
                  min={1}
                  adornment="products"
                  value={limit}
                  onChange={setLimit}
                  helperText="Maximum number of products to process"
                  disabled={hasTargetUrls}
                  size="medium"
                  sx={{ width: '100%' }}
                />

                <Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Product URLs (optional)</Typography>
                  <ChipInput
                    aria-label="Product URLs (optional)"
                    value={splitUrlDraft(urlsText)}
                    onChange={(next) => setUrlsText(next.join('\n'))}
                    validate={validateProductUrl}
                    placeholder="https://www.example.com/product/abc"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {hasTargetUrls
                      ? `${parsedUrls.length} URL${parsedUrls.length === 1 ? '' : 's'} will be scraped (full PDP). Category/limit ignored.`
                      : 'Paste one or more product page URLs (newline or comma separated) to scrape only those pages.'}
                  </Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Switch checked={hostImages} onChange={(e) => setHostImages(e.target.checked)} />
                  }
                  label="Host images on our infrastructure"
                />
              </>
            )}

            {cloudRunAvailable ? (
              <Alert severity="success" icon={<CheckCircle />}>
                Cloud Run is configured. Jobs will run automatically in the cloud.
              </Alert>
            ) : (
              <Alert severity="warning">
                Cloud Run is not configured. After creating the job, you&apos;ll need to run the scraper locally.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{triggerResult ? 'Close' : 'Cancel'}</Button>
        {canForceStart && (
          <Button color="warning" onClick={() => setForceConfirmOpen(true)} disabled={isPending}>
            Force start
          </Button>
        )}
        {!triggerResult && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isPending || hasRunningJob || (!isBulkJobMode && !selectedBrand)}
            startIcon={isPending ? <CircularProgress size={16} /> : <PlayArrow />}
          >
            {isPending
              ? 'Creating...'
              : isBulkJobMode
                ? `Start ${
                    jobMode === 'price_refresh'
                      ? 'Price Refresh'
                      : jobMode === 'stock_refresh'
                        ? 'Stock Refresh'
                        : 'Market Pricing Refresh'
                  }`
                : cloudRunAvailable
                  ? 'Start Ingestion'
                  : 'Create Job'}
          </Button>
        )}
      </DialogActions>
      <ForceStartConfirm
        open={forceConfirmOpen}
        targetName={selectedBrandInfo?.name || selectedBrand}
        runningBrandName={selectedBrandInfo?.name}
        runningSince={selectedBrandInfo?.runningSince}
        loading={isPending}
        onClose={() => setForceConfirmOpen(false)}
        onConfirm={() => {
          setForceConfirmOpen(false);
          onTrigger(selectedBrand, buildPayload(true));
        }}
      />
    </Dialog>
  );
};

export default GlobalTriggerDialog;
